// Grabacion de llamadas de WhatsApp desde el navegador.
//
// Junta el microfono del asesor (izquierda) y la voz del cliente (derecha) en
// un solo audio estereo, lo graba en webm/opus y lo envia al servidor en trozos
// de 2 s por el socket.io que ya abre el softphone (window.__ctiSfuSocket).
//
// Cada trozo se queda en la cola hasta que el servidor confirma que lo escribio.
// Si el socket se cae, al reconectar se vuelve a abrir la grabacion y se
// reenvian los pendientes en orden; el servidor ignora los repetidos.
//
// Todo es best-effort: si algo falla aqui, la llamada sigue igual.

const CHUNK_MS = 2000;
const ACK_TIMEOUT_MS = 10000;
const MIME_TYPES = ['audio/webm;codecs=opus', 'audio/webm'];
// Si el socket no vuelve, no se sigue intentando para siempre despues de colgar.
const DRAIN_AFTER_STOP_MS = 5 * 60 * 1000;

const getSocket = () => (typeof window !== 'undefined' ? window.__ctiSfuSocket : null);

export const recordingSupported = () => Boolean(
  typeof window !== 'undefined'
  && window.MediaRecorder
  && (window.AudioContext || window.webkitAudioContext)
  && MIME_TYPES.some((type) => window.MediaRecorder.isTypeSupported?.(type))
);

const emitWithAck = (socket, event, payload) => new Promise((resolve) => {
  if (!socket?.connected) {
    resolve({ ok: false, error: 'disconnected' });
    return;
  }
  socket.timeout(ACK_TIMEOUT_MS).emit(event, payload, (err, response) => {
    resolve(err ? { ok: false, error: 'timeout' } : (response || { ok: false, error: 'empty' }));
  });
});

export class WhatsAppCallRecording {
  /**
   * @param {object} p
   * @param {string} p.callId            call_id de Enigma (cti_log_calls).
   * @param {MediaStream} p.localStream  Microfono del asesor.
   * @param {MediaStream} p.remoteStream Voz del cliente.
   * @param {(state: {recording: boolean, pending: number, reconnecting: boolean, failed: boolean}) => void} [p.onState]
   */
  constructor({ callId, localStream, remoteStream, onState }) {
    this.callId = callId;
    this.localStream = localStream;
    this.remoteStream = remoteStream;
    this.onState = onState || (() => {});
    this.queue = [];        // [{ seq, blob }]
    this.nextSeq = 0;
    this.started = false;   // el servidor acepto wa-rec:start en este socket
    this.stopped = false;   // el MediaRecorder ya termino
    this.stopSent = false;
    this.failed = false;
    this.pumping = false;
    this.reason = 'stop';
    this.socket = null;
    this.onConnect = () => { this.started = false; this.pump(); };
    this.onDisconnect = () => { this.started = false; this.emitState(); };
    this.onSocketReady = () => this.attachSocket();
  }

  start() {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    const mimeType = MIME_TYPES.find((type) => window.MediaRecorder.isTypeSupported?.(type));
    this.mimeType = mimeType;
    this.context = new AudioCtx();
    this.context.resume?.().catch(() => {});

    // ChannelMerger convierte cada entrada a mono: entrada 0 = izquierda.
    const merger = this.context.createChannelMerger(2);
    this.context.createMediaStreamSource(this.localStream).connect(merger, 0, 0);
    this.context.createMediaStreamSource(this.remoteStream).connect(merger, 0, 1);
    const destination = this.context.createMediaStreamDestination();
    destination.channelCount = 2;
    merger.connect(destination);

    this.recorder = new MediaRecorder(destination.stream, { mimeType, audioBitsPerSecond: 64000 });
    this.recorder.ondataavailable = (event) => {
      if (!event.data || !event.data.size) return;
      this.queue.push({ seq: this.nextSeq, blob: event.data });
      this.nextSeq += 1;
      this.pump();
    };
    this.recorder.onstop = () => {
      this.stopped = true;
      this.pump();
    };
    this.recorder.start(CHUNK_MS);

    window.addEventListener('cti-socket-ready', this.onSocketReady);
    this.attachSocket();
    this.emitState();
  }

  /** Termina la captura. El envio sigue hasta vaciar la cola. */
  stop(reason = 'stop') {
    if (this.stopping) return;
    this.stopping = true;
    this.reason = String(reason || 'stop');
    try {
      if (this.recorder && this.recorder.state !== 'inactive') this.recorder.stop();
      else { this.stopped = true; this.pump(); }
    } catch (_) {
      this.stopped = true;
      this.pump();
    }
    this.context?.close?.().catch(() => {});
    this.drainTimer = setTimeout(() => this.dispose(), DRAIN_AFTER_STOP_MS);
  }

  attachSocket() {
    const socket = getSocket();
    if (!socket || socket === this.socket) { this.pump(); return; }
    if (this.socket) {
      this.socket.off('connect', this.onConnect);
      this.socket.off('disconnect', this.onDisconnect);
    }
    this.socket = socket;
    this.started = false;
    socket.on('connect', this.onConnect);
    socket.on('disconnect', this.onDisconnect);
    this.pump();
  }

  emitState() {
    this.onState({
      recording: !this.stopped && !this.failed,
      pending: this.queue.length,
      reconnecting: !this.failed && this.queue.length > 1 && !this.socket?.connected,
      failed: this.failed
    });
  }

  /** Envia en orden, de a un trozo, y solo lo suelta con la confirmacion. */
  async pump() {
    if (this.pumping || this.failed || this.disposed) return;
    this.pumping = true;
    let errored = false;
    try {
      while (!this.failed && !this.disposed) {
        const socket = this.socket || getSocket();
        if (!socket?.connected) break;
        if (!this.socket) this.attachSocket();

        if (!this.started) {
          const response = await emitWithAck(socket, 'wa-rec:start', { callId: this.callId, mimeType: this.mimeType });
          if (!response.ok) {
            if (['forbidden', 'unknown-call', 'invalid-call', 'closed'].includes(response.error)) this.fail(response.error);
            errored = true;
            break;
          }
          this.started = true;
          // El servidor ya tiene hasta nextSeq-1 (retoma tras reiniciar).
          const serverNext = Number(response.nextSeq) || 0;
          this.queue = this.queue.filter((item) => item.seq >= serverNext);
        }

        const item = this.queue[0];
        if (item) {
          const data = await item.blob.arrayBuffer();
          const response = await emitWithAck(socket, 'wa-rec:chunk', { callId: this.callId, seq: item.seq, data });
          if (response.ok) {
            if (this.queue[0] === item) this.queue.shift();
            this.emitState();
            continue;
          }
          if (response.error === 'no-session') { this.started = false; continue; }
          if (['forbidden', 'closed', 'max-duration', 'too-large'].includes(response.error)) this.fail(response.error);
          errored = true;
          break;
        }

        if (this.stopped && !this.stopSent) {
          const response = await emitWithAck(socket, 'wa-rec:stop', {
            callId: this.callId, lastSeq: this.nextSeq - 1, reason: this.reason
          });
          if (response.ok || response.error === 'no-session') {
            this.stopSent = true;
            this.dispose();
          } else {
            if (response.error === 'forbidden') this.fail(response.error);
            errored = true;
          }
        }
        break;
      }
    } finally {
      this.pumping = false;
      this.emitState();
    }
    // Queda trabajo: un trozo que llego mientras se enviaba otro (enseguida) o
    // un envio que fallo o sin socket (se reintenta en 2 s).
    const pendingWork = this.queue.length || (this.stopped && !this.stopSent);
    if (!this.failed && !this.disposed && pendingWork && !this.retryTimer) {
      const ready = !errored && this.socket?.connected && this.started;
      this.retryTimer = setTimeout(() => {
        this.retryTimer = null;
        this.pump();
      }, ready ? 0 : CHUNK_MS);
    }
  }

  fail(error) {
    this.failed = true;
    this.queue = [];
    console.warn('[WA-REC] grabacion detenida:', error);
    try { if (this.recorder && this.recorder.state !== 'inactive') this.recorder.stop(); } catch (_) {}
    this.emitState();
    this.dispose();
  }

  dispose() {
    if (this.disposed) return;
    this.disposed = true;
    clearTimeout(this.drainTimer);
    window.removeEventListener('cti-socket-ready', this.onSocketReady);
    if (this.socket) {
      this.socket.off('connect', this.onConnect);
      this.socket.off('disconnect', this.onDisconnect);
    }
  }
}
